import { SAVINGS_INTEREST_RATE, SAVINGS_LIMITS } from "shared/CalculatorLimits";
import { getSavingsPlan } from "shared/getSavingsPlan";
import { CalculatorRateAmount } from "web_myplann/Calculators/Components/CalculatorRateAmount";
import { CalculatorResultSection } from "web_myplann/Calculators/Components/CalculatorResultSection";
import { CalculatorShell } from "web_myplann/Calculators/Components/CalculatorShell";
import { CalculatorWaitingNote } from "web_myplann/Calculators/Components/CalculatorWaitingNote";
import { getSavingsFormSchema } from "web_myplann/Calculators/Forms/savingsFormSchema";
import { useCalculatorForm } from "web_myplann/Calculators/Hooks/useCalculatorForm";
import { useCalculatorLead } from "web_myplann/Calculators/Hooks/useCalculatorLead";
import { useReasonOptions } from "web_myplann/Calculators/Hooks/useReasonOptions";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import type { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import type { ReasonNode } from "web_myplann/Calculators/Types/ReasonNode";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { SliderFormRow } from "web_myplann/Shared/Components/SliderFormRow";
import { CurrencyCode } from "web_myplann/Shared/constants";

type Props<TReason extends string> = {
	type: CalculatorType;
	reasonLabel: string;
	reasons: readonly [TReason, ...TReason[]];
	reasonNode: ReasonNode<TReason>;
	yearsMin: number;
	yearsMax: number;
	yearsDefault: number;
};

export const SavingsCalculator = <TReason extends string>({
	type,
	reasonLabel,
	reasons,
	reasonNode,
	yearsMin,
	yearsMax,
	yearsDefault,
}: Props<TReason>) => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const { options: reasonOptions, getLeadValue } = useReasonOptions(
		reasons,
		reasonNode,
	);
	const { send, ...lead } = useCalculatorLead(type);
	const { control, watch, handleSubmit } = useCalculatorForm(
		(translate) => getSavingsFormSchema(translate, reasons),
		{ years: yearsDefault },
	);

	const { targetAmount, years } = watch();
	const { monthlyPayment, monthlyPaymentLater, yearDifference } =
		getSavingsPlan({
			years,
			targetAmount,
			interestRate: SAVINGS_INTEREST_RATE,
		});
	const hasResult =
		targetAmount >= SAVINGS_LIMITS.targetAmountMin &&
		targetAmount <= SAVINGS_LIMITS.targetAmountMax &&
		monthlyPayment > 0;

	const onSubmit = handleSubmit((values) =>
		send({
			amount: values.targetAmount,
			installment: monthlyPayment,
			interest: SAVINGS_INTEREST_RATE,
			period: values.years,
			purpose: getLeadValue(values.reason),
		}),
	);

	return (
		<CalculatorShell
			type={type}
			onSubmit={onSubmit}
			fields={
				<>
					<FlatRowFormInput
						control={control}
						name="targetAmount"
						label={t(Resources.Calculators.Savings.TargetAmount)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={SAVINGS_LIMITS.targetAmountMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<FlatRowFormSelect
						control={control}
						name="reason"
						label={reasonLabel}
						options={reasonOptions}
						placeholder={t(Resources.Calculators.SelectPlaceholder)}
					/>
					<SliderFormRow
						control={control}
						name="years"
						label={t(Resources.Calculators.Savings.Years)}
						formatValue={yearsLabel}
						min={yearsMin}
						max={yearsMax}
						isLast
					/>
				</>
			}
			result={
				hasResult && (
					<CalculatorResultSection
						badge={t(Resources.Calculators.Result.Badges.todaySave)}
						hero={
							<CalculatorRateAmount
								amount={monthlyPayment}
								rate={SAVINGS_INTEREST_RATE}
								rateFractionDigits={0}
							/>
						}
						waitingNote={
							yearDifference > 0 && monthlyPaymentLater > monthlyPayment ? (
								<CalculatorWaitingNote
									years={yearDifference}
									later={monthlyPaymentLater}
									extra={monthlyPaymentLater - monthlyPayment}
								/>
							) : undefined
						}
						lead={lead}
					/>
				)
			}
		/>
	);
};
